/**
 * @fileoverview Modal táctil para visualizar el Leaderboard Top 20 en vivo de Esquivador Personal.
 * Se sincroniza en tiempo real vía SignalR con nuevos puntajes y reinicios.
 * 
 * @module components/Dashboard/Sections/PersonalSurferRankingModal
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import SignalRService from '../../../utilities/WS/SignalRService';
import { GetLeaderboard } from '../../../utilities/DL/PersonalSurfer';
import { formatScore, getSurferBadge } from '../../../utilities/BL/PersonalSurfer';

export const PersonalSurferRankingModal = ({
    isOpen,
    onClose,
    roomId,
    currentParticipantId
}) => {
    const [rankingList, setRankingList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);

    const fetchRanking = useCallback(async () => {
        if (!roomId) return;
        setIsLoading(true);
        try {
            const res = await GetLeaderboard(roomId, 20);
            if (res && res.success && Array.isArray(res.data)) {
                setRankingList(res.data);
                setLastUpdated(new Date());
            }
        } catch (err) {
            console.warn('[PersonalSurferRankingModal] Error fetching ranking:', err);
        } finally {
            setIsLoading(false);
        }
    }, [roomId]);

    useEffect(() => {
        if (isOpen && roomId) {
            fetchRanking();
        }
    }, [isOpen, roomId, fetchRanking]);

    useEffect(() => {
        if (!isOpen || !roomId) return;

        const handleNewPlay = () => fetchRanking();
        const handleReset = () => fetchRanking();

        SignalRService.on('personalSurfer:newPlay', handleNewPlay);
        SignalRService.on('personalSurfer:reset', handleReset);

        return () => {
            SignalRService.off('personalSurfer:newPlay', handleNewPlay);
            SignalRService.off('personalSurfer:reset', handleReset);
        };
    }, [isOpen, roomId, fetchRanking]);

    if (!isOpen) return null;

    const renderMedal = (pos) => {
        if (pos === 1) return <span className="psl-surfer-ranking-modal__medal">🥇</span>;
        if (pos === 2) return <span className="psl-surfer-ranking-modal__medal">🥈</span>;
        if (pos === 3) return <span className="psl-surfer-ranking-modal__medal">🥉</span>;
        return <span className="psl-surfer-ranking-modal__pos-num">#{pos}</span>;
    };

    return (
        <div className="psl-surfer-modal-overlay" onClick={onClose}>
            <div
                className="psl-surfer-modal-card"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
            >
                {/* Cabecera */}
                <div className="psl-surfer-modal-card__header">
                    <div className="psl-surfer-modal-card__title-wrap">
                        <div className="psl-surfer-modal-card__live-indicator">
                            <span className="psl-surfer-modal-card__live-dot" />
                            <span>EN VIVO</span>
                        </div>
                        <h3 className="psl-surfer-modal-card__title">
                            🏆 Ranking del Estadio
                        </h3>
                    </div>
                    <button
                        type="button"
                        className="psl-surfer-modal-card__close-btn"
                        onClick={onClose}
                        aria-label="Cerrar modal"
                    >
                        <i className="bi bi-x-lg" />
                    </button>
                </div>

                {/* Subcabecera con hora de actualización */}
                <div className="psl-surfer-modal-card__sub-bar">
                    <span>Top 20 mejores corredores</span>
                    {lastUpdated && (
                        <span className="psl-surfer-modal-card__updated-time">
                            ⏱️ {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        </span>
                    )}
                </div>

                {/* Contenido / Lista de posiciones */}
                <div className="psl-surfer-modal-card__body">
                    {isLoading && rankingList.length === 0 ? (
                        <div className="psl-surfer-modal-card__loader">
                            <div className="spinner-border text-info" role="status" />
                            <span>Cargando posiciones...</span>
                        </div>
                    ) : rankingList.length === 0 ? (
                        <div className="psl-surfer-modal-card__empty">
                            <i className="bi bi-emoji-smile" />
                            <p>¡Aún no hay puntuaciones registradas!</p>
                            <small>Sé el primero en jugar y alcanzar el podio.</small>
                        </div>
                    ) : (
                        <div className="psl-surfer-ranking-list">
                            {rankingList.map((player, idx) => {
                                const pos = player.rankingPosition || player.position || (idx + 1);
                                const isCurrent = currentParticipantId && (player.id === currentParticipantId || player.participantId === currentParticipantId);
                                const badge = getSurferBadge(player.score || 0);

                                return (
                                    <div
                                        key={player.id || player.participantId || idx}
                                        className={`psl-surfer-ranking-item ${pos <= 3 ? `psl-surfer-ranking-item--top${pos}` : ''} ${isCurrent ? 'psl-surfer-ranking-item--current' : ''}`}
                                    >
                                        <div className="psl-surfer-ranking-item__pos">
                                            {renderMedal(pos)}
                                        </div>
                                        <div className="psl-surfer-ranking-item__info">
                                            <span className="psl-surfer-ranking-item__name">
                                                {player.participantName || player.name || `Corredor #${pos}`}
                                                {isCurrent && <span className="psl-surfer-ranking-item__you-tag"> (Tú)</span>}
                                            </span>
                                            <span className="psl-surfer-ranking-item__badge-label">
                                                {badge.icon} {badge.title}
                                            </span>
                                        </div>
                                        <div className="psl-surfer-ranking-item__score-wrap">
                                            <span className="psl-surfer-ranking-item__score-num">
                                                {formatScore(player.score || 0)}
                                            </span>
                                            <span className="psl-surfer-ranking-item__score-pts">pts</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Pie del modal */}
                <div className="psl-surfer-modal-card__footer">
                    <button
                        type="button"
                        className="psl-surfer-btn psl-surfer-btn--primary psl-surfer-btn--block"
                        onClick={onClose}
                    >
                        Entendido
                    </button>
                </div>
            </div>
        </div>
    );
};

PersonalSurferRankingModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    roomId: PropTypes.string.isRequired,
    currentParticipantId: PropTypes.string
};

export default PersonalSurferRankingModal;
